import { HEADER_MOBILE_BREAKPOINT } from '@app/constants/breakpoints';
import { AccountRequestStatus } from '@app/constants/requests';
import { getIsUserKnown } from '@app/ducks';
import { getVersions } from '@app/ducks/config';
import UserLogic from '@app/logic/User';
import { externalLinks, paths } from '@app/paths';
import { cn } from '@app/utils/cn';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useIsScreenSize, useModalVisibility, useOnEscHandler, usePageScrolling } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Menu as MenuNS, Navigation, User } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { RedesignExitSurveyModal } from '../onboarding';
import { Button } from '../various/Button';
import { ClientLogo } from '../various/ClientLogo';
import { FocusTrap } from '../various/FocusTrap';
import { CentraLogo } from '../various/Logo/Logos/CentraLogo';
import { Scrim } from '../various/Scrim';

import { MenuSection } from './MenuSection';
import { MenuToggle } from './MenuToggle';
import { PagesSection } from './PagesSection';

interface Props {
  isOpen: boolean;
  user: User | Empty.Object;
  lookbookEnabled: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  navigationItems?: Navigation.Item[];
  hasHeaderBanner?: boolean;
  shouldShowMenu?: boolean;
  logoUrl: string;
}

export const Menu = ({
  isOpen,
  user,
  lookbookEnabled,
  onOpenChange,
  navigationItems = [],
  hasHeaderBanner,
  shouldShowMenu = true,
  logoUrl,
}: Props) => {
  const { t } = useTranslation(['common', 'accounts', 'orders', 'selections', 'translations', 'settings', 'onboarding']);
  const { setPageScrollable } = usePageScrolling();
  const appVersion = useSelector(getVersions);
  const { key } = useLocation();
  const navigate = useNavigate();
  const isViewer = UserLogic.getIsViewer(user);
  const isBuyer = getIsUserKnown(user) && UserLogic.getIsBuyer(user);
  const isSeller = getIsUserKnown(user) && UserLogic.getIsSeller(user);
  const isAdmin = getIsUserKnown(user) && UserLogic.getIsAdmin(user);
  const isMobile = useIsScreenSize(HEADER_MOBILE_BREAKPOINT);
  const isPreviousDesignEnabled = getIsFeatureEnabled('ENABLE_PREVIOUS_DESIGN');
  const { showModal: showFeedbackModal } = useModalVisibility('FeedbackSurveyModal');
  const isLookbookWishlistEnabled = getIsFeatureEnabled('ENABLE_LOOKBOOK_WISHLIST');
  const isOnLookbookProductsPage = useMatch(paths.LOOKBOOK_SINGLE)?.pattern.end ?? false;
  const [isSwitchingToPreviousVersion, setIsSwitchingToPreviousVersion] = React.useState(false);
  const [isSwitchButtonLoading, setIsSwitchButtonLoading] = React.useState(false);

  const handleExitSurveyClose = () => {
    setIsSwitchingToPreviousVersion(false);
    setIsSwitchButtonLoading(true);
    switchToPreviousDesign();
  };

  const handleSwitchButtonClick = () => {
    setIsSwitchingToPreviousVersion(true);
  };

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPageScrollable(false);

    return () => {
      setPageScrollable(true);
    };
  }, [isOpen, setPageScrollable]);

  useOnEscHandler(document.documentElement, () => onOpenChange(false), isOpen);

  React.useEffect(() => {
    if (isOpen) {
      onOpenChange(false);
    }
  }, [key]);

  const generalItems: MenuNS.Item[] = React.useMemo(
    () => [
      {
        id: 'selectionsMenuLink',
        link: paths.SELECTIONS,
        name: t('selections:selection_other'),
      },
      {
        id: 'ordersMenuLink',
        link: paths.ORDERS,
        name: t('orders:order_other'),
      },
      {
        id: 'invoicesMenuLink',
        isHidden: !isBuyer,
        link: paths.INVOICES,
        name: t('common:invoice_other'),
      },
      {
        isHidden: !isSeller || !lookbookEnabled,
        link: paths.LOOKBOOK,
        name: t('common:lookbooks'),
      },
      {
        isHidden: !isSeller,
        link: paths.ACCOUNTS,
        name: t('accounts:account_other'),
      },
      {
        isHidden: !isSeller || !lookbookEnabled || !isLookbookWishlistEnabled,
        link: `${paths.ACCOUNT_REQUESTS}?status=${AccountRequestStatus.Pending}`,
        name: t('accounts:requests'),
      },
      {
        isHidden: !isSeller,
        link: paths.LINESHEETS,
        name: t('common:linesheet_other'),
      },
      {
        isHidden: !isAdmin,
        link: paths.PAGES_LIST,
        name: t('common:page_other'),
      },
      {
        isExternal: true,
        isHidden: isBuyer,
        link: externalLinks.SUPPORT,
        name: t('common:support'),
      },
    ],
    [t, isBuyer, isSeller, lookbookEnabled, isLookbookWishlistEnabled, isAdmin],
  );

  const settingsItems: MenuNS.Item[] = React.useMemo(
    () => [
      {
        link: paths.SETTINGS_CUSTOMIZATION,
        name: t('settings:navigation.customization.title'),
      },
      {
        link: paths.SETTINGS_NAVIGATION,
        name: t('settings:navigation.navigation.title'),
      },
      {
        link: paths.SETTINGS_TRANSLATIONS,
        name: t('settings:navigation.translations.title'),
      },
    ],
    [t],
  );

  const accountItems: MenuNS.Item[] = React.useMemo(
    () => [
      {
        link: paths.ACCOUNT_INFO,
        name: t('accounts:account_information'),
      },
      {
        link: paths.ACCOUNT_PASSWORD,
        name: t('accounts:navigation.password.title'),
      },
    ],
    [t],
  );

  const switchToPreviousDesign = () => {
    if (isOnLookbookProductsPage) {
      navigate(paths.LOOKBOOK);
    }
    // eslint-disable-next-line functional/immutable-data, @microsoft/sdl/no-cookies
    document.cookie = 'app-version=legacy; path=/; max-age=15782016';
    window.location.reload();
  };

  const handleLeaveFeedback = () => {
    showFeedbackModal();
    onOpenChange(false);
  };

  const inertProp = isOpen ? undefined : { inert: '' };

  const isMenuToggleVisible = shouldShowMenu && (!isViewer || (isMobile && !isEmpty(navigationItems)));
  const shouldShowVersionSwitch = !isViewer && isPreviousDesignEnabled;

  return (
    <>
      <div className="flex-1 shrink-0">
        <Scrim
          isVisible={isOpen}
          onClick={() => onOpenChange(false)}
          variant="light"
          className={cn({
            'top-[--menuScrimTopWithBanner]': hasHeaderBanner,
          })}
        />
        <FocusTrap isActive={isOpen}>
          <div
            {...inertProp}
            className={cx(
              'fixed bottom-0 left-0 top-0 z-navMenu flex w-full flex-col bg-white shadow-2 transition-transform sm:w-80 sm:rounded-e-regular',
              {
                '-translate-x-full': !isOpen,
                'mt-[--bannerHeight]': hasHeaderBanner,
              },
            )}
          >
            <div className="mt-[4.5rem] flex h-full flex-col gap-8 overflow-auto px-8">
              {isMobile && <PagesSection navigationItems={navigationItems} />}
              {!isViewer && <MenuSection items={generalItems} title={t('common:general')} />}
              {isAdmin && <MenuSection items={settingsItems} title={t('common:settings')} />}
              {!isViewer && <MenuSection items={accountItems} title={t('accounts:my_account')} />}
              <div className="space-y-3">
                {shouldShowVersionSwitch && (
                  <Button
                    size="medium"
                    variant="bordered"
                    color="secondary"
                    className="flex-shrink-0"
                    isLoading={isSwitchButtonLoading}
                    isFluid
                    onClick={handleSwitchButtonClick}
                  >
                    {t('onboarding:switch_to_previous_design')}
                  </Button>
                )}
                <Button
                  size="medium"
                  variant={shouldShowVersionSwitch ? 'ghost' : 'bordered'}
                  color="secondary"
                  className="flex-shrink-0"
                  isFluid
                  onClick={handleLeaveFeedback}
                >
                  {t('onboarding:survey.share_feedback')}
                </Button>
              </div>
              <div className="mb-8 mt-auto text-neutral-60 body-5">
                <div className="flex gap-2">
                  {t('common:powered_by')}
                  <CentraLogo className="-mt-0.5 h-5 w-12" />
                </div>
                {isDefined(appVersion.api) ?
                  <span>
                    v{appVersion.app} (API v{appVersion.api})
                  </span>
                : <span>v{appVersion.app}</span>}
              </div>
            </div>
          </div>
          <div className="relative z-navMenuHeader flex items-center gap-6">
            {isMenuToggleVisible && <MenuToggle isMenuOpened={isOpen} onClick={() => onOpenChange(!isOpen)} />}
            <ClientLogo url={logoUrl} />
          </div>
        </FocusTrap>
      </div>
      <RedesignExitSurveyModal isOpen={isSwitchingToPreviousVersion} onClose={handleExitSurveyClose} />
    </>
  );
};
